/**
 * Common Data Model — the top-level CDM page.
 *
 * Shows the rollup tile, a paginated golden-asset list, and quick links
 * to Investigation / Conflicts / Software / Vendor concentration / Adapters.
 *
 * See docs/cdm-design-v4.md and ADR-0007 for architecture.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Database, RefreshCw, AlertTriangle, Activity, Layers,
  Search, ChevronRight, Server, Laptop, Smartphone, Cloud, Filter,
  Play, Brain,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Overview {
  org_id: string
  summary: {
    asset_count: number
    user_count: number
    person_count: number
    software_install_count: number
    conflict_open_count: number
    orphan_unclaimed_count: number
    stale_adapter_count: number
    ghost_asset_count: number
    last_pull_at: string | null
    cdm_health: string
  }
}

interface Asset {
  golden_id: string
  archetype: string
  last_observed_at: string | null
  sources: string[]
  risk?: { total: number; band: string } | null
}

const ARCHETYPE_ICON: Record<string, React.ElementType> = {
  asset_laptop:   Laptop,
  asset_server:   Server,
  asset_mobile:   Smartphone,
  asset_cloud_vm: Cloud,
}

const ARCHETYPE_TONE: Record<string, string> = {
  asset_laptop:   'text-blue-light bg-blue/10 border-blue/30',
  asset_server:   'text-purple-light bg-purple/10 border-purple/30',
  asset_mobile:   'text-cyan bg-cyan/10 border-cyan/30',
  asset_cloud_vm: 'text-emerald bg-emerald/10 border-emerald/30',
}

export default function CdmPage() {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [archFilter, setArchFilter] = useState('')
  const [running, setRunning] = useState(false)
  const [runMessage, setRunMessage] = useState('')
  const [withRisk, setWithRisk] = useState(false)
  const [sortByRisk, setSortByRisk] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/cdm/overview'),
      api.get('/api/cdm/assets', { params: { limit: 200, with_risk: withRisk } }),
    ])
      .then(([o, a]) => {
        setOverview(o.data)
        setAssets(a.data?.assets || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [withRisk])

  const runAdapter = async (name: string) => {
    setRunning(true)
    setRunMessage('Running ' + name + '…')
    try {
      const { data } = await api.post(`/api/cdm/adapters/${name}/run`)
      const stats = data?.stats || {}
      setRunMessage(`${name}: ${stats.events_emitted ?? 0} events, ${stats.matched ?? 0} matched, ${stats.new_goldens ?? 0} new, ${stats.conflicts_opened ?? 0} conflicts (${data.elapsed_ms}ms)${data.error ? ' — ' + data.error : ''}`)
      load()
    } catch (e: any) {
      setRunMessage(`${name}: ${e.response?.data?.detail || 'failed'}`)
    } finally {
      setRunning(false)
    }
  }

  const runTuner = async () => {
    setRunning(true)
    setRunMessage('Running survivorship tuning agent…')
    try {
      const { data } = await api.post('/api/cdm/policy-proposals/agent/survivorship/run', null, { params: { dry_run: false } })
      setRunMessage(`Tuner: ${data.evidence_count} (archetype, field) tuples, ${data.proposals_drafted} drafts, ${data.proposals_inserted} ratified`)
    } catch (e: any) {
      setRunMessage(`Tuner: ${e.response?.data?.detail || 'failed'}`)
    } finally {
      setRunning(false)
    }
  }

  const archetypes = useMemo(() => {
    const s = new Set(assets.map(a => a.archetype))
    return Array.from(s).sort()
  }, [assets])

  const filtered = useMemo(() => {
    let result = assets
      .filter(a => !archFilter || a.archetype === archFilter)
      .filter(a => !search || a.golden_id.toLowerCase().includes(search.toLowerCase())
                          || a.sources.some(s => s.toLowerCase().includes(search.toLowerCase())))
    if (sortByRisk && withRisk) {
      result = [...result].sort((a, b) => (b.risk?.total ?? 0) - (a.risk?.total ?? 0))
    }
    return result
  }, [assets, archFilter, search, sortByRisk, withRisk])

  const s = overview?.summary

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Database size={18} className="text-emerald" /> COMMON DATA MODEL
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          asset graph · identity resolution · field provenance · write-back
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => runAdapter('crowdstrike')} disabled={running}
            className="btn-ghost" title="Run CrowdStrike CDM adapter pull (admin only)">
            <Play size={11} /> Run CrowdStrike
          </button>
          <button type="button" onClick={runTuner} disabled={running}
            className="btn-ghost" title="Run AI survivorship-rule tuner (admin only)">
            <Brain size={11} /> Run tuner
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {runMessage && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {runMessage}
        </div>
      )}

      {/* Rollup tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Tile label="Assets"           value={s?.asset_count ?? 0}           tone="text-emerald" />
        <Tile label="Users"             value={s?.user_count ?? 0}            tone="text-blue-light" />
        <Tile label="People"            value={s?.person_count ?? 0}          tone="text-purple-light" />
        <Tile label="Software installs" value={s?.software_install_count ?? 0} tone="text-cyan" />
        <Tile label="Open conflicts"    value={s?.conflict_open_count ?? 0}   tone="text-amber-light" warn={(s?.conflict_open_count ?? 0) > 0} />
        <Tile label="Stale adapters"    value={s?.stale_adapter_count ?? 0}   tone="text-rose-light" warn={(s?.stale_adapter_count ?? 0) > 0} />
      </div>

      {/* Quick links */}
      <div className="flex gap-2 text-xs flex-wrap">
        <QuickLink to="/app/cdm/conflicts"           icon={AlertTriangle} label="Conflicts" tone="text-amber-light border-amber/30" />
        <QuickLink to="/app/cdm/adapters"            icon={Activity}      label="Adapter health" tone="text-blue-light border-blue/30" />
        <QuickLink to="/app/cdm/software"            icon={Layers}        label="Software inventory" tone="text-cyan border-cyan/30" />
        <QuickLink to="/app/cdm/vendor-concentration" icon={Database}     label="Vendor concentration" tone="text-purple-light border-purple/30" />
      </div>

      {/* Asset filter + search */}
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={archFilter} onChange={e => setArchFilter(e.target.value)}
                aria-label="Filter by archetype">
          <option value="">All archetypes</option>
          {archetypes.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="search by golden_id or source"
            className="input pl-7 w-full"
          />
        </div>
        <button type="button" onClick={() => setWithRisk(!withRisk)}
          className={clsx('px-2 py-1 rounded border text-[11px]',
            withRisk ? 'bg-rose/10 text-rose-light border-rose/30' : 'border-border text-text-secondary')}>
          {withRisk ? '✓ Risk score' : 'Compute risk'}
        </button>
        {withRisk && (
          <button type="button" onClick={() => setSortByRisk(!sortByRisk)}
            className={clsx('px-2 py-1 rounded border text-[11px]',
              sortByRisk ? 'bg-amber/10 text-amber-light border-amber/30' : 'border-border text-text-secondary')}>
            {sortByRisk ? '↓ By risk' : 'Sort by risk'}
          </button>
        )}
        <span className="text-text-muted ml-auto">{filtered.length} of {assets.length}</span>
      </div>

      {/* Asset list */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Golden ID</th>
              <th className="text-left px-3 py-2">Archetype</th>
              <th className="text-left px-3 py-2">Sources</th>
              <th className="text-left px-3 py-2">Last observed</th>
              {withRisk && <th className="text-right px-3 py-2">Risk</th>}
              <th className="text-right px-3 py-2"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 && (
              <tr><td colSpan={withRisk ? 6 : 5} className="px-3 py-8 text-center text-text-muted">
                No assets yet. Configure a CDM adapter (e.g. CrowdStrike) under
                /app/connectors-manage and trigger a pull.
              </td></tr>
            )}
            {filtered.map(a => {
              const Icon = ARCHETYPE_ICON[a.archetype] || Database
              const rb = a.risk?.band
              const rt = a.risk?.total
              return (
                <tr key={a.golden_id} className="hover:bg-surface-raised/40">
                  <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">{a.golden_id.slice(0, 8)}…</td>
                  <td className="px-3 py-1.5">
                    <span className={clsx('badge text-[9px] border inline-flex items-center gap-1',
                                         ARCHETYPE_TONE[a.archetype] || 'bg-surface-raised text-text-muted border-border')}>
                      <Icon size={10} /> {a.archetype}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">
                    {a.sources.map(s => (
                      <span key={s} className="badge text-[9px] mr-1 bg-surface-raised text-text-muted border border-border">{s}</span>
                    ))}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">
                    {a.last_observed_at ? new Date(a.last_observed_at).toLocaleString() : '—'}
                  </td>
                  {withRisk && (
                    <td className="px-3 py-1.5 text-right">
                      {rt !== undefined && rt !== null ? (
                        <span className={clsx('font-mono font-bold text-xs',
                          rb === 'critical' ? 'text-rose-light' :
                          rb === 'high'     ? 'text-amber-light' :
                          rb === 'medium'   ? 'text-cyan' :
                                              'text-emerald')}>
                          {rt}
                        </span>
                      ) : <span className="text-text-muted">—</span>}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right">
                    <Link to={`/app/cdm/investigation/${a.golden_id}`} className="text-blue-light hover:underline inline-flex items-center gap-1">
                      Investigate <ChevronRight size={11} />
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Last pull: {s?.last_pull_at ? new Date(s.last_pull_at).toLocaleString() : 'never'} ·
        CDM health: <span className={clsx(
          s?.cdm_health === 'healthy' ? 'text-emerald' : 'text-text-muted'
        )}>{s?.cdm_health || 'unknown'}</span>
      </p>
    </div>
  )
}


function Tile({ label, value, tone, warn }: { label: string; value: number; tone: string; warn?: boolean }) {
  return (
    <div className={clsx('card p-3', warn && 'ring-1 ring-amber/40')}>
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', tone)}>{value.toLocaleString()}</div>
    </div>
  )
}


function QuickLink({ to, icon: Icon, label, tone }: { to: string; icon: React.ElementType; label: string; tone: string }) {
  return (
    <Link to={to} className={clsx(
      'inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] hover:bg-surface-raised',
      tone,
    )}>
      <Icon size={11} /> {label}
    </Link>
  )
}
